import type { ReactElement, ReactNode } from 'react';
import type React from 'react';
import { Fragment, useState } from 'react';

import type { ReportFileType, ReportType } from 'src/constants/report';
import ButtonPanel from 'src/components/common/ButtonPanel';
import Modal from 'src/components/common/Modal';
import { WorkspaceContext } from 'src/components/workspace/WorkspaceContext';

import ConfigureReportForm from './components/ConfigureReportForm';

export interface CurrentReport {
  fileType: ReportFileType;
  reportName: string;
  reportType: ReportType;
  language: number;
}
interface ConfigureReportComponentProps {
  afterSubmit: any;
  buttonIcon: ReactNode;
  buttonAndHeadlineText: string;
  headerIcon: ReactElement;
  currentReport: CurrentReport;
  setReportConfiguration: (config: any) => void;
  reportNameOnly?: boolean;
  validateReportConfiguration?: () => boolean;
  getDriverAnalysisSet: () => void;
}

export const ConfigureReportComponent: React.FC<
  ConfigureReportComponentProps
> = ({
  afterSubmit,
  buttonIcon,
  headerIcon,
  buttonAndHeadlineText,
  currentReport,
  setReportConfiguration,
  reportNameOnly = false,
  validateReportConfiguration = () => true,
  getDriverAnalysisSet,
}) => {
  const currentConfigurationValues: CurrentReport = {
    fileType: currentReport.fileType,
    language: currentReport.language,
    reportName: currentReport.reportName,
    reportType: currentReport.reportType,
  };
  const [isModalOpen, setModalOpen] = useState(false);

  // prevent blur being triggered upon closing the modal
  // todo: currently not working as intended: This handler should prevent a layout shift
  //  when clicking on the close icon but as many components have been replaced by
  //  mui-components it stopped working. A quickfix was not possible therefore it was
  //  postponed due to time limitations.
  const handleInputBlur = (event: any, defaultHandleBlur: any) => {
    defaultHandleBlur(event);
  };

  return (
    <Fragment>
      <ButtonPanel
        onClick={() => {
          if (validateReportConfiguration()) setModalOpen(true);
        }}
      >
        {buttonIcon}

        <span className="configure-report-button__label">
          {buttonAndHeadlineText}
        </span>
      </ButtonPanel>

      <Modal
        headline={buttonAndHeadlineText}
        icon={headerIcon}
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
      >
        <ConfigureReportForm
          afterSubmit={afterSubmit}
          closeModal={() => setModalOpen(false)}
          currentValues={currentConfigurationValues}
          handleInputBlur={handleInputBlur}
          reportNameOnly={reportNameOnly}
          setReportConfiguration={setReportConfiguration}
          submitButtonText={buttonAndHeadlineText}
          getDriverAnalysisSet={getDriverAnalysisSet}
        />
      </Modal>
    </Fragment>
  );
};

const ConfigureReportWrapper = (props: any) => (
  <WorkspaceContext.Consumer>
    {(context) =>
      context ? (
        <ConfigureReportComponent
          currentReport={context.currentReport}
          getDriverAnalysisSet={context.getDriverAnalysisSet}
          setReportConfiguration={context.setReportConfiguration}
          validateReportConfiguration={context.validateReportConfiguration}
          {...props}
        />
      ) : null
    }
  </WorkspaceContext.Consumer>
);

export default ConfigureReportWrapper;
